import { useState } from 'react';

import type { ArInvoiceWithLines, CreateArInvoiceVoidInput } from '@dcc-erp/shared';
import { AR_VOID_REASONS, CreateArInvoiceVoidSchema, dateToInputValue } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { modalBody, modalError, modalFooter, modalLabel } from './modal-style';
import {
  useActiveCompanyId,
  useRequestArInvoiceVoid,
  useSubmitArInvoiceVoid,
} from '../../lib/queries';
import { AppModal } from '../AppModal';

// =============================================================================
// Void an AR invoice (ยกเลิกใบแจ้งหนี้, AR-07 / Phase 2c).
//
// VoidInvoiceModal (container) owns the two-step request-then-submit engine
// flow; the inner VoidInvoiceForm (presentational, router/data-free) owns the
// reason select + note + void-date so the submit payload is unit-testable
// without a query client. Mirrors the CreateCreditNoteModal split + chrome.
// A void cancels a posted, UNTOUCHED invoice (no payments, no credit notes);
// on final approval the engine posts the inverse JV and flips status → VOID.
// Plain prototype.css classes / inline modal-style — no tailwind.
// =============================================================================

// Reason options sourced from the shared preset table so the codes stay in one
// place (reasonCode drives reporting; label is display-only).
const reasonOptions = Object.entries(AR_VOID_REASONS).map(([value, label]) => ({
  value,
  label,
}));

// The form only reads the invoice's id; accept the full detail shape but depend
// on the lean subset so tests can pass lean fixtures.
type VoidInvoice = Pick<ArInvoiceWithLines, 'id'>;

export interface VoidInvoiceFormProps {
  invoice: VoidInvoice;
  onSubmit: (payload: CreateArInvoiceVoidInput) => void;
  submitting: boolean;
}

export function VoidInvoiceForm({ invoice, onSubmit, submitting }: VoidInvoiceFormProps) {
  const [voidDate, setVoidDate] = useState(dateToInputValue(new Date()));
  const [reasonCode, setReasonCode] = useState<string>(reasonOptions[0]?.value ?? '');
  const [reasonNote, setReasonNote] = useState('');

  const disabled = !reasonCode || !voidDate || submitting;

  function handleSubmit() {
    onSubmit({
      invoiceId: invoice.id,
      voidDate,
      reasonCode,
      ...(reasonNote ? { reasonNote } : {}),
    });
  }

  return (
    <div style={modalBody}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={modalLabel}>วันที่ยกเลิก</label>
          <input
            type="date"
            className="input"
            aria-label="void-date"
            value={voidDate}
            onChange={(e) => setVoidDate(e.target.value)}
            style={{ width: '100%', height: 38, fontSize: 14 }}
          />
        </div>
        <div>
          <label style={modalLabel}>เหตุผล</label>
          <select
            className="select"
            aria-label="void-reason"
            value={reasonCode}
            onChange={(e) => setReasonCode(e.target.value)}
            style={{ width: '100%', height: 38, fontSize: 14 }}
          >
            {reasonOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label style={modalLabel}>หมายเหตุ (optional)</label>
        <textarea
          className="input"
          aria-label="void-reason-note"
          value={reasonNote}
          onChange={(e) => setReasonNote(e.target.value)}
          placeholder="รายละเอียดเพิ่มเติม"
          rows={3}
          style={{ width: '100%', fontSize: 14, padding: 8, resize: 'vertical' }}
        />
      </div>

      <div className="muted" style={{ fontSize: 13 }}>
        การยกเลิกจะส่งเข้าสายอนุมัติ (4-eyes)
        เมื่ออนุมัติครบจะกลับรายการบัญชีอัตโนมัติและเปลี่ยนสถานะใบแจ้งหนี้เป็น "ยกเลิก"
      </div>

      <div style={modalFooter}>
        <button type="button" className="btn primary" disabled={disabled} onClick={handleSubmit}>
          <Icon name="check" size={14} /> {submitting ? 'กำลังบันทึก…' : 'ส่งคำขอยกเลิก'}
        </button>
      </div>
    </div>
  );
}

export interface VoidInvoiceModalProps {
  invoice: ArInvoiceWithLines;
  open: boolean;
  onClose: () => void;
}

export function VoidInvoiceModal({ invoice, open, onClose }: VoidInvoiceModalProps) {
  const companyId = useActiveCompanyId();
  const request = useRequestArInvoiceVoid(companyId);
  const submit = useSubmitArInvoiceVoid(companyId);

  const [validationError, setValidationError] = useState<string | null>(null);
  // Holds the id of the DRAFT void once requestVoid succeeds. If submitVoid then
  // throws, the DRAFT is already created server-side; on retry we must reuse this
  // id (skip requestVoid) so we don't orphan a second DRAFT — the server blocks a
  // second in-progress void with a 400, so a naive re-request would just confuse.
  const [createdId, setCreatedId] = useState<string | null>(null);

  // Fresh open starts clean: a void created in a prior open must not leak its id
  // into a new invoice's flow. Reset during render on the closed→open edge (the
  // store-previous-prop pattern) rather than in an effect, so there's no extra
  // render pass. handleClose also clears it on the normal close path.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open && createdId !== null) setCreatedId(null);
  }

  const error = request.error ?? submit.error;
  const pending = request.isPending || submit.isPending;
  // A DRAFT exists but the submit step failed — surface a retry-specific message.
  const submitOnlyFailed = createdId !== null && submit.error != null;

  function handleClose() {
    setCreatedId(null);
    onClose();
  }

  async function handleSubmit(payload: CreateArInvoiceVoidInput) {
    // Validate the wire payload against the shared schema before POSTing so a
    // malformed request never leaves the client.
    const parsed = CreateArInvoiceVoidSchema.safeParse(payload);
    if (!parsed.success) {
      setValidationError(parsed.error.issues[0]?.message ?? 'ข้อมูลไม่ถูกต้อง');
      return;
    }
    setValidationError(null);
    // Two-step engine flow, idempotent on retry: request the DRAFT void (only if
    // one wasn't already created on a prior failed attempt), then submit it into
    // the approval engine (the reversing JV is booked on final approval, not here).
    // A thrown mutation is recorded on request.error / submit.error by react-query
    // and surfaced in the banner; swallow it here so the rejection isn't unhandled
    // and onClose only fires on full success.
    try {
      let id = createdId;
      if (id === null) {
        const created = await request.mutateAsync(parsed.data);
        id = created.id;
        setCreatedId(id);
      }
      await submit.mutateAsync(id);
    } catch {
      // Stay open; the hook's error (request/submit) drives the banner + retry.
      return;
    }
    // Full success — clear the draft id so a later reopen starts clean.
    setCreatedId(null);
    onClose();
  }

  if (!open) return null;

  // WS8 R2 — standard AppModal (1200px) in place of the bespoke 760px chrome.
  // Container-only: VoidInvoiceForm keeps its body + in-body submit button.
  return (
    <AppModal title="ยกเลิกใบแจ้งหนี้" onClose={handleClose}>
      <VoidInvoiceForm
        invoice={invoice}
        onSubmit={(payload) => void handleSubmit(payload)}
        submitting={pending}
      />
      {(validationError ?? error) &&
        (submitOnlyFailed && !validationError ? (
          <div style={{ ...modalError, padding: '0 22px 16px' }}>
            สร้างคำขอแล้ว แต่ส่งอนุมัติไม่สำเร็จ — กดส่งอีกครั้งเพื่อส่งอนุมัติ
          </div>
        ) : (
          <div style={{ ...modalError, padding: '0 22px 16px' }}>
            บันทึกไม่สำเร็จ: {validationError ?? error?.message ?? ''}
          </div>
        ))}
    </AppModal>
  );
}
